import { Map as ImmutableMap } from 'immutable';
import type { Map as ImmutableMapType } from 'immutable';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useRef } from 'react';

import { categoriesSelectors } from 'app-components/flux/categories';
import type { Category } from 'app-components/flux/categories/categoriesTypes';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { transactionsActions, transactionsSelectors } from 'app-components/flux/transactions';
import type { CashFlowTransaction, TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import ZeroStateView from 'components/ZeroStateView';
import { makeStyles } from 'tss-react/mui';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { boxShadowsToDropShadows } from 'themes/createTheme';

import RefundImage from 'assets/illustrations/Refund.png';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogTitle from 'components/Dialogs/StdDialogTitle';
import { useStandardDrawerVariant } from 'components/Drawers/StandardDrawer';
import {
  getRootModalRemoveDialogOptions,
  ROOT_MODAL_DRAWER_PROP,
  ROOT_MODAL_PRESENTATION_PROP,
  type RootModalDrawerOptions,
} from 'components/Drawers/standardDrawerContract';
import QPanelButton from 'components/QPanelButton';
import AmountField from 'components/QuickenControls/AmountField';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

export const DIALOG_TYPE_REFUND_MATCH = 'DIALOG_TYPE_REFUND_MATCH';

type ShowRefundMatchDialogOptions = {
  rootModalDrawer?: RootModalDrawerOptions;
  transaction?: CashFlowTransaction | TransactionRecord;
};

type RefundMatchDialogProps = ShowRefundMatchDialogOptions & {
  dialogId?: string;
  onRootModalDrawerClose?: () => void;
};

type StdDialogRef = {
  closeDialog: (reason?: string) => void;
};

export const showRefundMatchDialog = ({ rootModalDrawer, transaction }: ShowRefundMatchDialogOptions = {}) => {
  return rootUiActions.createDialog(
    rootUiTypes.mkRootUiData({
      id: 'refund-match',
      type: DIALOG_TYPE_REFUND_MATCH,
      allowNesting: true,
      props: ImmutableMap({
        [ROOT_MODAL_DRAWER_PROP]: rootModalDrawer,
        [ROOT_MODAL_PRESENTATION_PROP]: rootModalDrawer ? undefined : ('modal' as const),
        transaction,
      }),
    }),
  );
};

const useStyles = makeStyles()((theme) => ({
  receiptShadow: {
    filter: boxShadowsToDropShadows(theme.shadows[3]),
    transition: '300ms',
    '&:hover': {
      filter: boxShadowsToDropShadows(theme.shadows[5]),
    },
  },
  panelButton: {
    clipPath: theme.shape.bottomCutOffClipPath,
    boxShadow: 'none',
  },
  payee: {
    width: 160,
    margin: 8,
    textAlign: 'left',
  },
  date: {
    width: 100,
    margin: 8,
  },
  amount: {
    width: 60,
    margin: 8,
  },
}));

const RefundMatchDialog = (props: RefundMatchDialogProps): React.JSX.Element => {
  const { dialogId, onRootModalDrawerClose, rootModalDrawer, transaction } = props;

  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const drawerVariant = useStandardDrawerVariant();
  const showDrawerBackButton = Boolean(rootModalDrawer && drawerVariant === 'overlay');

  const refundActiveTransactions = useAppSelector((state) =>
    transactionsSelectors.getRefundActiveTransactionsByAccountId(state, transaction?.accountId ?? ''),
  );
  const categoryIds = useMemo(
    () =>
      refundActiveTransactions
        .valueSeq()
        .filter((txn) => txn.coa?.type === 'CATEGORY')
        .map((txn) => txn.coa?.id)
        .filter((id): id is string => Boolean(id))
        .toArray(),
    [refundActiveTransactions],
  );
  const categories: ImmutableMapType<string, Category> = useAppSelector((state) =>
    categoriesSelectors.getCategoriesForIds(state, categoryIds),
  );
  const showCategoryIcons = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'categoryIcons'));

  const stdDialogRef = useRef<StdDialogRef>(null);
  const closeDialog = useCallback(
    (reason?: string) => {
      if (rootModalDrawer) {
        if (onRootModalDrawerClose) {
          onRootModalDrawerClose();
          return;
        }

        dispatch(
          rootUiActions.removeDialog(dialogId ?? 'refund-match', getRootModalRemoveDialogOptions(rootModalDrawer)),
        );
        return;
      }

      stdDialogRef.current?.closeDialog(reason);
    },
    [dialogId, dispatch, onRootModalDrawerClose, rootModalDrawer],
  );

  const content = (
    <DialogContent sx={rootModalDrawer ? { flex: '1 1 auto', minHeight: 0 } : undefined}>
      {Boolean(!refundActiveTransactions.size) && (
        <ZeroStateView
          size='small'
          description="We couldn't find any refund tracking records"
          qIllustration='boxEmpty'
        />
      )}

      {Boolean(refundActiveTransactions.size) && (
        <Box display='flex' paddingRight={12}>
          <Box m={3}>
            <img alt='refund tracker' src={RefundImage} width={72} height={72} />
          </Box>
          <Box display='flex' flexDirection='column'>
            <Typography>Select the corresponding refund for this transaction.</Typography>

            {refundActiveTransactions.toIndexedSeq().map((refundTransaction, index) => (
              <Slide
                key={refundTransaction?.id}
                in
                appear
                direction='left'
                style={{ transitionDelay: `${index * 30}ms` }}
              >
                <div className={classes.receiptShadow}>
                  <QPanelButton
                    id={`refund-id-${index}`}
                    key={refundTransaction.id}
                    onClick={() => {
                      dispatch(
                        transactionsActions.performTransactionAction(
                          {
                            action: 'match',
                            id: refundTransaction.id ?? '',
                            otherId: transaction?.id ?? '',
                          },
                          { undo: { userMessage: 'Refund matched.' } },
                        ),
                      );
                      closeDialog('refundMatched');
                    }}
                    className={classes.panelButton}
                    startIcon={
                      <Avatar
                        aria-label='category logo'
                        variant='rounded'
                        src={
                          showCategoryIcons && refundTransaction.coa?.id
                            ? categories.get(refundTransaction.coa.id)?.logoUrl
                            : undefined
                        }
                      >
                        {refundTransaction?.payee?.[0]?.toUpperCase?.()}
                      </Avatar>
                    }
                    title={
                      <Box display='flex'>
                        <Typography className={classes.payee}>{refundTransaction.payee}</Typography>

                        <Typography className={classes.date}>
                          {DateTime.fromISO(refundTransaction.postedOn).toLocaleString(DateTime.DATE_MED)}
                        </Typography>

                        <Typography align='right' className={classes.amount}>
                          <AmountField value={refundTransaction.amount} />
                        </Typography>
                      </Box>
                    }
                  />
                </div>
              </Slide>
            ))}
          </Box>
        </Box>
      )}
    </DialogContent>
  );

  if (rootModalDrawer) {
    return (
      <Box
        aria-label='Match refund'
        role='dialog'
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
      >
        <StdDialogTitle
          variant='drawer'
          id='dialog-title'
          onBack={showDrawerBackButton ? () => closeDialog('back') : undefined}
          onClose={() => closeDialog('close')}
          showBackButton={showDrawerBackButton}
          showCloseButton
        >
          Match refund
        </StdDialogTitle>
        {content}
      </Box>
    );
  }

  return (
    <StdDialog
      stdDialogRef={stdDialogRef}
      sharedcomponentid='RefundMatchDialog'
      size='md'
      title='Match refund'
      {...props}
    >
      {content}
    </StdDialog>
  );
};

export default RefundMatchDialog;
